/**
 * Post-match screen.
 *
 * Every award here was decided by the server from the recorded combat log. The
 * model only ever supplies the short title next to a winner it did not pick —
 * and when there is no model, rule-based titles take its place.
 */
import { useState } from 'react';
import { C2S } from '@spell/network-protocol';
import { net } from '../networking/net';
import { useNet, selectOpponent, selectSelf } from '../networking/netStore';
import { sfx } from '../audio/sfx';

const AWARD_LABEL: Record<string, string> = {
  'best-attack': '최고의 공격 주문',
  'best-parry': '최고의 패링 주문',
  'most-accurate': '가장 정확하게 시전한 주문',
  'most-blocked': '가장 많은 피해를 막은 주문',
  decisive: '결정타를 기록한 주문',
  'spell-of-the-match': 'SPELL OF THE MATCH',
};

export function MatchResult({ onLeave }: { onLeave: () => void }) {
  const result = useNet((s) => s.matchResult);
  const self = useNet(selectSelf);
  const opponent = useNet(selectOpponent);
  const [voted, setVoted] = useState<null | boolean>(null);

  if (!result) return null;

  const won = result.winnerId === self?.sessionId;
  const hero = result.awards.find((a) => a.key === 'spell-of-the-match');
  const rest = result.awards.filter((a) => a.key !== 'spell-of-the-match');

  const vote = (keepSpells: boolean) => {
    setVoted(keepSpells);
    sfx.ui();
    net.send(C2S.REMATCH, { keepSpells });
  };

  return (
    <div className="screen">
      <div className="panel result" data-testid="match-result">
        <h1 className="title" style={{ color: won ? 'var(--gold)' : 'var(--ink-dim)' }}>
          {result.winnerId === null ? '무승부' : won ? '승리' : '패배'}
        </h1>
        <p className="subtitle">
          {self?.name} {self ? (result.scores[self.sessionId] ?? 0) : 0} — {opponent ? (result.scores[opponent.sessionId] ?? 0) : 0}{' '}
          {opponent?.name}
        </p>

        {hero ? (
          <div className="award award--hero" data-testid="spell-of-the-match">
            <div className="award__key">{AWARD_LABEL[hero.key]}</div>
            <div className="award__title">{hero.title}</div>
            <div className="award__chant">{hero.chant}</div>
            <div className="award__detail">
              {hero.detail}
              {hero.ownerId === self?.sessionId ? ' · 당신의 주문' : ' · 상대의 주문'}
            </div>
          </div>
        ) : null}

        <div className="award-grid">
          {rest.map((award) => (
            <div className="award" key={award.key}>
              <div className="award__key">{AWARD_LABEL[award.key] ?? award.key}</div>
              <div className="award__title">{award.title}</div>
              <div className="award__chant">{award.chant}</div>
              <div className="award__detail">
                {award.detail}
                {award.ownerId === self?.sessionId ? ' · 당신' : ' · 상대'}
              </div>
            </div>
          ))}
        </div>

        <div className="section-label">이번 경기에 사용된 주문</div>
        {result.ownSpells.map((spell) => (
          <div className="spell-card" key={spell.id}>
            <div className="spell-card__head">
              <span className="spell-card__chant" style={{ color: spell.visuals.color }}>
                {spell.chant}
              </span>
              <span className="spell-card__tag" style={{ color: spell.visuals.color }}>
                {spell.type === 'parry' ? '패링' : '시그니처'}
              </span>
              <span className="hint">{spell.interpreter === 'ai' ? 'AI 해석' : '로컬 해석기'}</span>
            </div>
            <div className="hint" style={{ marginTop: 4 }}>
              {spell.summary}
            </div>
            <div className="hint" style={{ marginTop: 4, fontFamily: 'var(--font-mono)' }}>
              마나 {spell.stats.manaCost} · 재사용 {(spell.stats.cooldownMs / 1000).toFixed(1)}s ·
              {spell.stats.damage > 0 ? ` 피해 ${spell.stats.damage} ·` : ''} 규칙 v{spell.balanceVersion}
            </div>
          </div>
        ))}

        <div className="section-label">상대가 사용한 주문 (공개 정보)</div>
        {result.spells
          .filter((s) => s.ownerId !== self?.sessionId)
          .map((s) => (
            <div className="spell-card" key={s.id}>
              <div className="spell-card__head">
                <span className="spell-card__chant" style={{ color: s.color }}>
                  {s.chant}
                </span>
                <span className="spell-card__tag" style={{ color: s.color }}>
                  {s.type === 'parry' ? '패링' : '시그니처'}
                </span>
              </div>
              <div className="hint" style={{ marginTop: 4 }}>
                {s.publicSummary}
              </div>
            </div>
          ))}

        <div className="section-label">다시 대련</div>
        <div className="row row--wrap">
          <button
            type="button"
            className={`btn ${voted === true ? 'btn--primary' : ''}`}
            data-testid="rematch-keep"
            onClick={() => vote(true)}
          >
            현재 주문 유지하고 재대결
          </button>
          <button
            type="button"
            className={`btn ${voted === false ? 'btn--primary' : ''}`}
            data-testid="rematch-reset"
            onClick={() => vote(false)}
          >
            주문 초기화하고 새 대결
          </button>
          <button type="button" className="btn btn--ghost" onClick={onLeave}>
            로비로 나가기
          </button>
        </div>
        <div className="hint" style={{ marginTop: 10 }}>
          {voted === null
            ? '두 명이 모두 선택하면 시작합니다.'
            : opponent?.rematchReady
              ? '상대도 선택했습니다. 곧 시작합니다…'
              : '상대의 선택을 기다리는 중…'}
          {' 한쪽이라도 초기화를 고르면 양쪽 모두 초기화된 상태로 시작합니다.'}
        </div>
      </div>
    </div>
  );
}
